import React, { ReactElement, memo, useMemo, RefObject, useCallback } from "react";
import cx from "classnames";
import { isPopFlow } from "@swiggy-private/checkout-core";
import { TEXTS } from "@cart-ui/constants";

//helpers
import { format, ScreenReaderOnly, AriaHidden, useEffectOnce } from "@swiggy-private/core-ui";

// test ids
import { CART_FOOTER_TEST_IDS, SLOT_TEST_IDS } from "@swiggy-private/cart-test-ids";

//styles
import { Footer, FooterFirst, MakePaymentButton, ViewBillText, SelectDeliverySlotButton } from "./styles";

//components
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

export interface CartPaymentFooterProps {
    cartTotal: number;
    tipAmount?: number;
    viewDetailedBill: () => void;
    makePayment: () => void;
    classNames?: {
        container?: string;
        primaryButton?: string;
        secondaryButton?: string;
    };
    canPlaceOrderFromCart?: boolean;
    makePaymentButtonRef?: RefObject<HTMLDivElement>;
    isSlotEnabled?: boolean;
    selectDeliverySlot: Function;
    impressionProceedToPay: Function;
    impressionSelectDeliverySlotButton: Function;
}

const ACCESSIBILITY_TEXTS = {
    CART_TOTAL: "Cart total is {0} Rupees",
};

function CartPaymentFooter(props: CartPaymentFooterProps): ReactElement {
    const { canPlaceOrderFromCart } = props;
    const isPopCart = isPopFlow();
    const accessibilityText = useMemo(() => {
        return `${format(ACCESSIBILITY_TEXTS.CART_TOTAL, String(props.cartTotal))} ${
            props.tipAmount && props.tipAmount > 0 ? format(TEXTS.WITH_X_TIP, String(props.tipAmount)) : ""
        }`;
    }, [props.cartTotal, props.tipAmount]);
    const onClickShowSlots = useCallback(() => {
        props.selectDeliverySlot();
    }, [props]);

    useEffectOnce(() => {
        if (props.isSlotEnabled) {
            props.impressionSelectDeliverySlotButton();
        } else {
            props.impressionProceedToPay();
        }
    });

    return (
        <Footer className={cx(props.classNames?.container)}>
            <ScreenReaderOnly text={accessibilityText} />
            <FooterFirst onClick={props.viewDetailedBill} className={cx(props.classNames?.primaryButton)}>
                <AriaHidden as="div">
                    <Text
                        variant="V2.subheading1.neutral.bold"
                        color="text_Highest_Emphasis"
                        data-testid={CART_FOOTER_TEST_IDS.BILL_TOTAL}
                    >
                        {`₹${props.cartTotal}`}
                    </Text>
                </AriaHidden>
                <ViewBillText
                    variant="V2.body3.neutral.bold"
                    color="special_positive"
                    data-testid={CART_FOOTER_TEST_IDS.VIEW_DETAILED_BILL_BTN}
                >
                    {TEXTS.VIEW_DETAILED_BILL_CAPITALIZED}
                </ViewBillText>
            </FooterFirst>
            {props.isSlotEnabled ? (
                <SelectDeliverySlotButton
                    onClick={onClickShowSlots}
                    className={cx(props.classNames?.secondaryButton)}
                    data-testid={SLOT_TEST_IDS.SELECT_DELIVERY_SLOT_BUTTON}
                    isPopCart={isPopCart}
                >
                    {TEXTS.SELECT_DELIVERY_SLOT}
                </SelectDeliverySlotButton>
            ) : (
                <MakePaymentButton
                    onClick={props.makePayment}
                    className={cx(props.classNames?.secondaryButton)}
                    aria-label={canPlaceOrderFromCart ? "Place order" : "Make payment, tap to go to payment"}
                    ref={props.makePaymentButtonRef}
                    tabIndex={-1}
                    data-testid={CART_FOOTER_TEST_IDS.MAKE_PAYMENT}
                    isPopCart={isPopCart}
                >
                    <Text
                        variant="V2.subheading1.neutral.bold"
                        color={isPopCart ? "text_Highest_Emphasis" : "text_inverse_Highest_Emphasis"}
                    >
                        {canPlaceOrderFromCart ? TEXTS.PLACE_ORDER : TEXTS.PROCEED_TO_PAY}
                    </Text>
                </MakePaymentButton>
            )}
        </Footer>
    );
}

export default memo(CartPaymentFooter);
